import React, { useCallback, useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import FaceScanMesh from "./FaceScanMesh";
import API_BASE_URL from "../config";
import "./LiveSyncPage.css";

/**
 * Desktop-only console for Live Folder Sync.
 *
 * The engine itself runs in Electron's main process and keeps working with this
 * window closed; this page is where a studio watches it. It is styled as a
 * monitoring console rather than a settings form because that is what it is —
 * the thing you glance at mid-shoot to confirm photos are still landing.
 */
function LiveSyncPage() {
	// Match every other screen: open on a desktop-width window, collapsible.
	// This page previously opened with the sidebar hidden *and* hid its own
	// toggle above 900px, which left no way to reach the menu at all.
	const [sidebarOpen, setSidebarOpen] = useState(
		typeof window !== "undefined" ? window.innerWidth > 768 : true,
	);
	const [watches, setWatches] = useState([]);
	const [rawPrompt, setRawPrompt] = useState(null);
	const [error, setError] = useState("");

	const [adding, setAdding] = useState(false);
	const [events, setEvents] = useState([]);
	const [selectedEvent, setSelectedEvent] = useState("");
	const [folders, setFolders] = useState([]);
	const [selectedFolder, setSelectedFolder] = useState("");
	const [mode, setMode] = useState("compressed");
	const [subscriptionType, setSubscriptionType] = useState("");
	const [droppedDir, setDroppedDir] = useState(null);
	const [dragOver, setDragOver] = useState(false);
	const [launchAtLogin, setLaunchAtLogin] = useState(false);
	const [faceStatus, setFaceStatus] = useState(null);

	const liveSync = window.photentra?.liveSync;

	const refresh = useCallback(() => {
		liveSync?.list().then(setWatches).catch(() => {});
	}, [liveSync]);

	useEffect(() => {
		if (!liveSync) return undefined;
		refresh();
		return liveSync.onEvent((event) => {
			if (event.kind === "status") setWatches(event.watches);
			if (event.kind === "raw-decision-needed") {
				setRawPrompt({ watchId: event.watchId, filename: event.filename });
			}
			if (event.kind === "sync-error") {
				setError(event.message);
				setTimeout(() => setError(""), 6000);
			}
		});
	}, [liveSync, refresh]);

	useEffect(() => {
		window.photentra?.getLaunchAtLogin?.().then(setLaunchAtLogin).catch(() => {});
	}, []);

	// Face data is either computed here or left to the server. Poll while a sync
	// is running, because the models finish downloading part-way through one.
	useEffect(() => {
		if (!liveSync?.faceStatus) return undefined;
		const read = () => liveSync.faceStatus().then(setFaceStatus).catch(() => {});
		read();
		const timer = setInterval(read, 30000);
		return () => clearInterval(timer);
	}, [liveSync]);

	useEffect(() => {
		const userId = localStorage.getItem("userId");
		const token = localStorage.getItem("token");
		if (!userId) return;

		fetch(`${API_BASE_URL}/api/events/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
			.then((r) => r.json())
			.then((data) => setEvents(Array.isArray(data.events) ? data.events : []))
			.catch(() => setEvents([]));

		fetch(`${API_BASE_URL}/api/user/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
			.then((r) => r.json())
			.then((u) => setSubscriptionType(u.subscription_type || ""))
			.catch(() => {});
	}, []);

	useEffect(() => {
		setFolders([]);
		setSelectedFolder("");
		if (!selectedEvent) return;
		fetch(`${API_BASE_URL}/api/events/${selectedEvent}/folders`, {
			headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
		})
			.then((r) => r.json())
			.then((data) => setFolders(Array.isArray(data.folders) ? data.folders : []))
			.catch(() => setFolders([]));
	}, [selectedEvent]);

	const onDrop = (e) => {
		e.preventDefault();
		setDragOver(false);
		const file = e.dataTransfer?.files?.[0];
		if (!file) return;
		const dirPath = window.photentra.getPathForFile(file);
		if (!dirPath) return;
		setDroppedDir(dirPath);
		setAdding(true);
	};

	const startAdd = async () => {
		if (!selectedEvent) return;
		const dirPath =
			droppedDir ||
			(await window.photentra.chooseFolder({
				title: "Choose the folder your camera or card reader writes into",
				buttonLabel: "Watch this folder",
			}));
		if (!dirPath) return;

		const eventObj = events.find((e) => String(e.id) === String(selectedEvent));
		const folderObj = folders.find((f) => String(f.id) === String(selectedFolder));

		try {
			await liveSync.add({
				dirPath,
				eventId: Number(selectedEvent),
				eventName: eventObj ? eventName(eventObj) : `Event ${selectedEvent}`,
				folderId: selectedFolder ? Number(selectedFolder) : null,
				folderName: folderObj?.name || null,
				mode,
			});
			closeAdd();
			refresh();
		} catch (err) {
			setError(err.message || "Could not start watching that folder");
			setTimeout(() => setError(""), 6000);
		}
	};

	const closeAdd = () => {
		setAdding(false);
		setDroppedDir(null);
	};

	const answerRaw = (backup) => {
		if (!rawPrompt) return;
		liveSync.answerRawBackup(rawPrompt.watchId, backup);
		setRawPrompt(null);
	};

	const toggleLaunchAtLogin = async () => {
		const next = await window.photentra.setLaunchAtLogin(!launchAtLogin);
		setLaunchAtLogin(next);
	};

	if (!liveSync) {
		return (
			<div className="ls-layout">
				<Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
				<main className={`ls-main ${sidebarOpen ? "" : "sidebar-closed"}`}>
					<h1>Live Folder Sync</h1>
					<p className="ls-browser-note">
						Live Sync watches a folder on your computer and uploads new photos as they
						appear — it needs the Photentra desktop app.
					</p>
				</main>
			</div>
		);
	}

	const isProMax = subscriptionType === "pro_max_ultra";
	const active = watches.filter((w) => w.armed && !w.paused);
	const uploading = watches.filter((w) => w.uploading);
	const totalSynced = watches.reduce((n, w) => n + (w.counts?.uploaded || 0) + (w.counts?.server || 0), 0);
	const totalQueued = watches.reduce((n, w) => n + (w.pendingCount || 0), 0);
	const anyError = watches.some((w) => w.lastError);

	const ledState = anyError ? "error" : uploading.length > 0 || active.length > 0 ? "active" : watches.length > 0 ? "paused" : "idle";
	const statusText = anyError
		? "Connection problem — retrying"
		: uploading.length > 0
			? `Uploading from ${uploading.length} folder${uploading.length === 1 ? "" : "s"}`
			: active.length > 0
				? `Watching ${active.length} folder${active.length === 1 ? "" : "s"}`
				: watches.length > 0
					? "All folders paused"
					: "Standing by";

	return (
		<div
			className={`ls-layout ${dragOver ? "drag-over" : ""}`}
			onDragOver={(e) => {
				e.preventDefault();
				setDragOver(true);
			}}
			onDragLeave={() => setDragOver(false)}
			onDrop={onDrop}
		>
			<Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

			<main className={`ls-main ${sidebarOpen ? "" : "sidebar-closed"}`}>
				<button
					className="ls-menu-toggle"
					onClick={() => setSidebarOpen((v) => !v)}
					aria-label="Toggle menu"
				>
					☰
				</button>

				<header className="ls-hero">
					<FaceScanMesh active={active.length > 0} size={158} />

					<div className="ls-hero-copy">
						<p className="ls-eyebrow">Photentra Desktop · Core Engine</p>
						<h1>Live Folder Sync</h1>
						<p>
							Point Photentra at the folder your camera, card reader, or tether writes
							into. New photos are detected, compressed, and uploaded to the event as
							they’re shot — even with this window closed.
						</p>
						<div className="ls-status-line">
							<span className="ls-led" data-state={ledState} />
							{statusText}
						</div>
					</div>
				</header>

				<div className="ls-stats">
					<div className="ls-stat">
						<div className="ls-stat-value accent">{totalSynced.toLocaleString()}</div>
						<div className="ls-stat-label">Photos synced</div>
					</div>
					<div className="ls-stat">
						<div className="ls-stat-value">{active.length}</div>
						<div className="ls-stat-label">Folders watched</div>
					</div>
					<div className="ls-stat">
						<div className="ls-stat-value">{totalQueued.toLocaleString()}</div>
						<div className="ls-stat-label">In queue</div>
					</div>
				</div>

				{faceLine(faceStatus) && (
					<div className="ls-face-note">
						<span className="ls-led" data-state={faceStatus?.loaded ? "active" : "idle"} />
						{faceLine(faceStatus)}
					</div>
				)}

				<div className="ls-toolbar">
					<div className="ls-toolbar-left">
						<label className="ls-toggle">
							<input type="checkbox" checked={launchAtLogin} onChange={toggleLaunchAtLogin} />
							<span>Start with my computer</span>
						</label>
						<button className="ls-link-btn" onClick={() => window.photentra.showLogs()}>
							View sync log
						</button>
					</div>
					<button className="livesync-add-btn" onClick={() => setAdding(true)}>
						+ Watch a folder
					</button>
				</div>

				{error && <div className="ls-error">{error}</div>}
				{dragOver && <div className="ls-drop-hint">Drop a folder to start watching it</div>}

				{watches.length === 0 && !adding ? (
					<div className="ls-empty">
						<div style={{ display: "flex", justifyContent: "center" }}>
							<FaceScanMesh active={false} size={112} />
						</div>
						<h3>No folders being watched</h3>
						<p>
							Choose a folder and Photentra will upload every new photo that lands in it,
							automatically, for as long as the app is running.
						</p>
						<button className="livesync-add-btn" onClick={() => setAdding(true)}>
							Watch your first folder
						</button>
					</div>
				) : (
					<ul className="ls-nodes">
						{watches.map((w) => (
							<li
								key={w.id}
								className={`ls-node ${w.paused ? "paused" : ""}`}
								data-uploading={Boolean(w.uploading)}
							>
								<div className="ls-node-body">
									<div className="ls-node-title">
										<span className="ls-folder" title={w.dirPath}>{basename(w.dirPath)}</span>
										<span className="ls-arrow">→</span>
										<span className="ls-target">
											{w.eventName}
											{w.folderName ? ` / ${w.folderName}` : ""}
										</span>
									</div>
									<div className="ls-node-status">
										<span className="ls-led" data-state={nodeLed(w)} />
										{statusLine(w)}
										{w.lastError && <span className="ls-node-error">— {w.lastError}</span>}
									</div>
									{w.uploading && w.progress && (
										<div className="ls-progress">
											<div
												className="ls-progress-fill"
												style={{ width: `${Math.round((w.progress.done / w.progress.total) * 100)}%` }}
											/>
										</div>
									)}
								</div>

								<div className="ls-node-actions">
									<span className={`ls-chip ${w.mode}`}>
										{w.mode === "original" ? "Original" : "Compressed"}
									</span>
									{w.paused ? (
										<button onClick={() => liveSync.resume(w.id)}>▶ Resume</button>
									) : (
										<button onClick={() => liveSync.pause(w.id)}>⏸ Pause</button>
									)}
									<button
										className="ls-remove"
										onClick={() => {
											if (window.confirm("Stop watching this folder? Photos already uploaded stay in the event.")) {
												liveSync.remove(w.id);
											}
										}}
									>
										✕
									</button>
								</div>
							</li>
						))}
					</ul>
				)}

				{adding && (
					<div className="ls-modal-overlay" onClick={closeAdd}>
						<div className="ls-modal" onClick={(e) => e.stopPropagation()}>
							<h2>Watch a folder</h2>
							{droppedDir && <p className="ls-dropped-dir">{droppedDir}</p>}

							<label>
								Upload into event
								<select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
									<option value="">Choose an event…</option>
									{events.map((ev) => (
										<option key={ev.id} value={ev.id}>{eventName(ev)}</option>
									))}
								</select>
							</label>

							{folders.length > 0 && (
								<label>
									Event folder (optional)
									<select value={selectedFolder} onChange={(e) => setSelectedFolder(e.target.value)}>
										<option value="">General</option>
										{folders.map((f) => (
											<option key={f.id} value={f.id}>{f.name}</option>
										))}
									</select>
								</label>
							)}

							<fieldset className="ls-mode">
								<legend>Upload quality</legend>
								<label className={mode === "compressed" ? "selected" : ""}>
									<input
										type="radio"
										name="mode"
										checked={mode === "compressed"}
										onChange={() => setMode("compressed")}
									/>
									<span>
										<strong>Compressed{isProMax ? " (recommended)" : ""}</strong>
										{isProMax
											? " — high-quality 4K JPEGs, faster and seamless during a shoot."
											: " — great quality, fast uploads."}
									</span>
								</label>
								<label className={mode === "original" ? "selected" : ""}>
									<input
										type="radio"
										name="mode"
										checked={mode === "original"}
										onChange={() => setMode("original")}
										disabled={!isProMax}
									/>
									<span>
										<strong>Original</strong>
										{isProMax
											? " — untouched files; slower and heavier on data."
											: " — available on Pro Max."}
									</span>
								</label>
							</fieldset>

							<div className="ls-modal-actions">
								<button onClick={closeAdd}>Cancel</button>
								<button className="primary" disabled={!selectedEvent} onClick={startAdd}>
									{droppedDir ? "Start watching" : "Choose folder…"}
								</button>
							</div>
						</div>
					</div>
				)}

				{rawPrompt && (
					<div className="ls-modal-overlay">
						<div className="ls-modal" onClick={(e) => e.stopPropagation()}>
							<h2>RAW files detected</h2>
							<p>
								<strong>{rawPrompt.filename}</strong> appeared in a watched folder. Should
								Photentra also back up RAW originals from this folder? JPEG previews are
								uploaded either way.
							</p>
							<div className="ls-modal-actions">
								<button onClick={() => answerRaw(false)}>Skip RAW files</button>
								<button className="primary" onClick={() => answerRaw(true)}>
									Back up RAW originals
								</button>
							</div>
						</div>
					</div>
				)}
			</main>
		</div>
	);
}

function basename(p) {
	return p.split(/[\\/]/).filter(Boolean).pop() || p;
}

function eventName(ev) {
	if (ev.event_name) return ev.event_name;
	if (ev.name) return ev.name;
	const couple = [ev.groom_name, ev.bridal_name].filter(Boolean).join(" & ");
	return couple || `Event ${ev.id}`;
}

/**
 * Where face data for these uploads is being computed. Worth saying out loud:
 * doing it here is what keeps a big event from queueing behind the server, and
 * when it is not happening the studio should know nothing is broken — the
 * server simply does it instead.
 */
function faceLine(status) {
	if (!status) return null;
	if (status.loaded) return "Face data is being prepared on this computer — faster galleries, lighter servers.";
	if (!status.models?.configured) return null;
	if (status.models?.lastError) return "Preparing face data on the server for now — this computer will take over once its setup finishes.";
	return "Getting this computer ready to prepare face data; the server is handling it meanwhile.";
}

function nodeLed(w) {
	if (w.lastError) return "error";
	if (w.paused) return "paused";
	if (w.armed) return "active";
	return "idle";
}

function statusLine(w) {
	if (w.paused) return "Paused";
	if (!w.armed) return "Waiting for sign-in";
	if (w.uploading && w.progress) return `Uploading ${w.progress.done}/${w.progress.total}…`;
	if (w.pendingCount > 0) return `${w.pendingCount} queued`;
	if (w.parkedRawCount > 0) return `${w.parkedRawCount} RAW file(s) awaiting your choice`;
	const synced = (w.counts?.uploaded || 0) + (w.counts?.server || 0);
	return `Watching — ${synced} synced${w.counts?.failed ? `, ${w.counts.failed} failed (will retry)` : ""}`;
}

export default LiveSyncPage;
